/**
 * Plánovač času – výpočet, jak dlouho před začátkem akce vyrazit.
 *
 * Vzorec:
 *   předstih = rezerva_začátku + stavba_jeviště + stěhování + cesta + rezerva_odjezdu
 *
 * Cesta je defaultně km × time_per_km min/km,
 * ale lze ji za místo přepsat (např. dle mapy.cz nebo reálné zkušenosti).
 */

/**
 * @param {Object} place                 záznam místa (vč. extra_reserve_min)
 * @param {Object} settings              globální nastavení
 * @param {number} [eventExtraMin]       extra rezerva pro konkrétní akci (default 0)
 * @returns {{pilsenMin:number, stribroMin:number, breakdown:Object}}
 */
export function calcLeadTime(place, settings, eventExtraMin = 0) {
    const placeExtra    = place?.extra_reserve_min    ?? 0;
    const reserveStart  = place?.reserve_start_min    ?? (Number(settings.time_reserve_start)      || 20);
    const stageSetup    = place?.stage_setup_min      ?? (Number(settings.time_stage_setup)        || 40);
    const moving        = place?.moving_min           ?? (Number(settings.time_moving)             || 10);
    const reserveDepart = place?.reserve_depart_min   ?? (Number(settings.time_reserve_departure)  || 5);
    const perKm         = Number(settings.time_per_km) || 1;

    const meetingMin = reserveStart + stageSetup + moving + placeExtra + (eventExtraMin || 0);
    const fixed      = meetingMin + reserveDepart;

    const travelPilsen  = place?.travel_time_pilsen  ?? (place?.distance_pilsen  || 0) * perKm;
    const travelStribro = place?.travel_time_stribro ?? (place?.distance_stribro || 0) * perKm;

    return {
        meetingMin,
        pilsenMin:  fixed + travelPilsen,
        stribroMin: fixed + travelStribro,
        breakdown: { reserveStart, stageSetup, moving, placeExtra, eventExtra: eventExtraMin || 0, travelPilsen, travelStribro, reserveDepart }
    };
}

/**
 * Vrátí efektivní trasu skupiny Stříbro pro dané místo.
 * 'via_pilsen' = jedou do Plzně a přesedají do dodávky
 * 'direct'     = jedou přímo na místo
 */
export function getStribroRoute(place, settings) {
    if (place?.stribro_route) return place.stribro_route;
    const dp = place?.distance_pilsen  || 0;
    const ds = place?.distance_stribro || 0;
    const THRESHOLD = 15;
    if (dp - ds > THRESHOLD) return 'via_stribro';  // dodávka z Plzně jede přes Stříbro
    if (ds - dp > THRESHOLD) return 'via_pilsen';   // Stříbro jede do Plzně, sedí do dodávky
    return 'direct';
}

/**
 * Km, které Stříbro-muzikant ujede vlastním autem.
 * via_pilsen = Stříbro→Plzeň (35 km), via_stribro = 0 (nastupují v Stříbru), direct = přímá trasa.
 */
export function getStribroKm(place, settings) {
    const route = getStribroRoute(place, settings);
    if (route === 'via_pilsen')  return Number(settings.distance_stribro_pilsen) || 35;
    if (route === 'via_stribro') return 0;
    return place?.distance_stribro || 0;
}

/**
 * Z času akce vrátí čas odjezdu (HH:MM).
 * @param {string} eventTime "19:00"
 * @param {number} leadMin   předstih v minutách
 */
export function calcDepartureTime(eventTime, leadMin) {
    if (!eventTime) return null;
    const [h, m] = eventTime.split(':').map(Number);
    const totalMin = h * 60 + m - leadMin;
    // záporné = den předem, kladné = stejný den
    const normalized = ((totalMin % 1440) + 1440) % 1440;
    const oh = Math.floor(normalized / 60);
    const om = normalized % 60;
    return `${String(oh).padStart(2, '0')}:${String(om).padStart(2, '0')}`;
}
